import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Product } from '../../shared/types';
import { staggerItem } from '../utils/animations';
import { formatCurrency } from '../../shared/utils/currency';
import { trackAddToCart } from '../../shared/utils/metaPixel';
import QuickAddModal from './QuickAddModal';
import { useWishlist } from '../../shared/context/WishlistContext';
import { useCart } from '../../shared/context/CartContext';
import { useCartDrawer } from '../../shared/context/CartDrawerContext';
import { ExpandedProduct } from '../utils/productExpansion';

interface ProductCardProps {
  product: Product | ExpandedProduct;
  variantId?: string;
  isNew?: boolean;
  isBestseller?: boolean;
  isLarge?: boolean;
}

// Styled Components
const CardContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 300px;
  text-decoration: none;
  color: ${props => props.theme.colors.text.primary};
  box-sizing: border-box;
  overflow: hidden;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-height: 250px;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    min-height: 200px;
  }
`;

const ImageWrapper = styled.div<{ $isLarge?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: auto;
  aspect-ratio: ${props => props.$isLarge ? '21 / 9' : '4 / 5'};
  overflow: hidden;
  background-color: ${props => props.theme.colors.white};
  margin-bottom: ${props => props.theme.spacing.xs};
  user-select: none;
  touch-action: pan-y;
  border: none;
  box-sizing: border-box;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    aspect-ratio: ${props => props.$isLarge ? '16 / 9' : '4 / 5'};
  }
  
  ${props => props.$isLarge && `
    @media (min-width: ${props.theme.breakpoints.tablet}) {
      min-height: 500px;
      aspect-ratio: 21 / 9;
    }
    @media (max-width: ${props.theme.breakpoints.tablet}) {
      aspect-ratio: 16 / 9;
      min-height: 200px;
    }
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    max-width: 100%;
  `}
`;

const ImageContainer = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Image = styled(motion.img)<{ $isLarge?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$isLarge ? 'cover' : 'contain'};
  pointer-events: none;
  background: ${props => props.theme.colors.white};
`;

const Video = styled(motion.video)<{ $isLarge?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$isLarge ? 'cover' : 'contain'};
  pointer-events: none;
  background: ${props => props.theme.colors.white};
`;

const ImageOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0);
  pointer-events: none;
  transition: background-color ${props => props.theme.transitions.base};
`;

const DiscountBadge = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 6px;
  background-color: #F81212;
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: 9px;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  letter-spacing: 0.3px;
  text-transform: uppercase;
  z-index: 3;
  border-radius: 3px;
  line-height: 1.2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 8px;
    padding: 2px 5px;
  }
`;

const ActionButtonsContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 2;
`;

const WishlistButton = styled(motion.button)<{ $isInWishlist: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: ${props => props.theme.colors.black};
    fill: ${props => props.$isInWishlist ? props.theme.colors.black : 'none'};
    stroke-width: 2;
  }
`;

const AddToCartButton = styled(motion.button)`
  position: absolute;
  top: 48px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0;
  transition: all 0.2s ease;
  
  ${CardContainer}:hover & {
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
    stroke: ${props => props.theme.colors.black};
    fill: none;
    stroke-width: 2;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    opacity: 1;
  }
`;

const ProductInfo = styled.div<{ $isLarge?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  
  ${props => props.$isLarge && `
    padding-left: ${props.theme.spacing.md};
    padding-right: ${props.theme.spacing.md};
    
    @media (max-width: ${props.theme.breakpoints.mobile}) {
      padding-left: ${props.theme.spacing.sm};
      padding-right: ${props.theme.spacing.sm};
    }
  `}
`;

const ProductTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.normal};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${props => props.theme.spacing.xs};
`;

const Price = styled.span`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.normal};
  color: ${props => props.theme.colors.text.primary};
`;





const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  margin-top: ${props => props.theme.spacing.sm};
  opacity: 1;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.base};
  white-space: nowrap;
  
  ${props => props.$variant === 'primary' ? `
    background-color: ${props.theme.colors.brand?.red || props.theme.colors.text.primary} !important;
    color: #ffffff !important;
    border: 2px solid ${props.theme.colors.brand?.red || props.theme.colors.text.primary};
    
    &, &:hover, &:active, &:focus, &:visited {
      color: #ffffff !important;
      background-color: ${props.theme.colors.brand?.red || props.theme.colors.text.primary} !important;
    }
    
    &:hover {
      background-color: ${props.theme.colors.brand?.redDark || props.theme.colors.text.secondary} !important;
      border-color: ${props.theme.colors.brand?.redDark || props.theme.colors.text.secondary};
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
    }
  ` : `
    background-color: transparent;
    color: ${props.theme.colors.text.primary};
    border: 1px solid ${props.theme.colors.text.primary};
    
    &:hover, &:active, &:focus {
      background-color: ${props.theme.colors.brand?.red || props.theme.colors.text.primary};
      border-color: ${props.theme.colors.brand?.red || props.theme.colors.text.primary};
      color: #ffffff !important;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavArrow = styled.button<{ $direction: 'left' | 'right' }>`
  position: absolute;
  ${props => props.$direction === 'left' ? 'left' : 'right'}: ${props => props.theme.spacing.xs};
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0;
  transition: opacity ${props => props.theme.transitions.base};
  
  ${ImageWrapper}:hover & {
    opacity: 1;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    opacity: 1;
  }
  
  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    ${props => props.$direction === 'left' 
      ? 'border-width: 6px 8px 6px 0; border-color: transparent #000 transparent transparent;'
      : 'border-width: 6px 0 6px 8px; border-color: transparent transparent transparent #000;'
    }
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 100%);
  backdrop-filter: blur(8px);
  color: ${props => props.theme.colors.white};
  padding: 5px 11px;
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: 10px;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  border-radius: 14px;
  z-index: 2;
  opacity: 0;
  transition: opacity ${props => props.theme.transitions.base}, transform ${props => props.theme.transitions.base};
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  ${ImageWrapper}:hover & {
    opacity: 1;
    transform: translateY(-2px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    opacity: 1;
    font-size: 9px;
    padding: 4px 9px;
  }
  
  span:first-child {
    font-weight: ${props => props.theme.typography.fontWeight.bold};
  }
  
  span:last-child {
    opacity: 0.7;
  }
`;

const HeartIcon: React.FC<{ filled?: boolean }> = ({ filled = false }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "#000000" : "none"}
    stroke="#000000"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  variantId: propVariantId,
  isNew = false,
  isBestseller = false,
  isLarge = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState<'add' | 'buy'>('add');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const dragStartX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const { addToWishlist, removeFromWishlist, isInWishlist, items: wishlistItems } = useWishlist();
  const { addToCart } = useCart();
  const { openDrawer: openCartDrawer } = useCartDrawer();

  // Track media types (image or video)
  const [mediaTypes, setMediaTypes] = useState<('image' | 'video')[]>([]);

  const discountPercent = product.compareAtPrice && product.compareAtPrice > product.price
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  // Use variantId from props if provided (from ExpandedProduct), otherwise use first variant
  const variantId = propVariantId || (product as Product).variants?.[0]?.id || product.id;
  const isWishlisted = isInWishlist(product.id, variantId);

  const handleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isWishlisted) {
        const wishlistItem = wishlistItems?.find(
          (item) => item.productId === product.id && 
          (!variantId || item.variantId === variantId)
        );
        if (wishlistItem) {
          await removeFromWishlist(wishlistItem.id);
        }
      } else {
        await addToWishlist(product.id, variantId);
      }
    } catch (error) {
      console.error('Failed to toggle wishlist:', error);
    }
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only add with a real variant when product has variants (prevents orders with missing variant)
    const prod = product as Product;
    const hasVariants = prod.variants && prod.variants.length > 0;
    const validVariantId = hasVariants
      ? (prod.variants?.some((v: { id: string }) => v.id === variantId) ? variantId : undefined)
      : variantId;
    if (hasVariants && !validVariantId) return; // Require variant selection
    try {
      await addToCart(product.id, validVariantId, 1);
      
      // Track TikTok Pixel AddToCart
      trackAddToCart({
        id: product.id,
        title: product.title,
        price: product.price,
        quantity: 1,
        variantId: validVariantId,
      });
      
      openCartDrawer();
    } catch (error) {
      console.error('Failed to add to cart:', error);
    }
  };

  // Get all available media (images + videos)
  const images = (product.imageUrls && product.imageUrls.length > 0) ? product.imageUrls : [];
  const videos = (product.videoUrls && product.videoUrls.length > 0) ? product.videoUrls : [];
  const allMedia = [...images, ...videos];
  const allImages = allMedia; // No placeholder fallback
  
  // Set media types on mount or when product changes
  useEffect(() => {
    const types: ('image' | 'video')[] = [
      ...images.map(() => 'image' as const),
      ...videos.map(() => 'video' as const)
    ];
    setMediaTypes(types.length > 0 ? types : ['image']);
  }, [product.id]);
  
  const hasMultipleImages = allImages.length > 1;
  const currentImage = allImages[currentImageIndex] || allImages[0];
  const currentMediaType = mediaTypes[currentImageIndex] || 'image';

  // Preload additional images when user hovers over the card
  useEffect(() => {
    if (isHovered && !imagesPreloaded && hasMultipleImages) {
      // Preload remaining images (skip first one as it's already loaded)
      allImages.slice(1).forEach((imageUrl, index) => {
        if (mediaTypes[index + 1] === 'image') {
          const img = document.createElement('img');
          img.src = imageUrl;
        }
      });
      setImagesPreloaded(true);
    }
  }, [isHovered, imagesPreloaded, hasMultipleImages, allImages, mediaTypes]);

  // Auto-scroll disabled - images only change on user interaction
  // useEffect(() => {
  //   if (autoScrollIntervalRef.current) {
  //     clearInterval(autoScrollIntervalRef.current);
  //     autoScrollIntervalRef.current = null;
  //   }

  //   if (hasMultipleImages && !isPaused && !isHovered) {
  //     autoScrollIntervalRef.current = setInterval(() => {
  //       setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  //     }, 2000);
  //   }

  //   return () => {
  //     if (autoScrollIntervalRef.current) {
  //       clearInterval(autoScrollIntervalRef.current);
  //       autoScrollIntervalRef.current = null;
  //     }
  //   };
  // }, [hasMultipleImages, isPaused, isHovered, allImages.length]);

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product.id]);

  // Navigation functions
  const goToNextImage = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsPaused(true);
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    setTimeout(() => setIsPaused(false), 3000);
  };

  const goToPrevImage = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsPaused(true);
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    setTimeout(() => setIsPaused(false), 3000);
  };

  // Swipe/drag handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.type === 'mousedown') {
      dragStartX.current = (e as React.MouseEvent).clientX;
    } else {
      dragStartX.current = (e as React.TouchEvent).touches[0].clientX;
    }
    isDragging.current = true;
    setIsPaused(true);
  };

  const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current) return;
    
    let endX: number;
    if (e.type === 'mouseup') {
      endX = (e as React.MouseEvent).clientX;
    } else {
      endX = (e as React.TouchEvent).changedTouches[0].clientX;
    }

    const diff = dragStartX.current - endX;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        goToNextImage();
      } else {
        goToPrevImage();
      }
    }

    isDragging.current = false;
    setTimeout(() => setIsPaused(false), 3000);
  };

  return (
    <CardContainer
      variants={staggerItem}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link 
        to={`/products/${product.handle || product.id}`} 
        style={{ textDecoration: 'none', color: 'inherit' }}
        className="product-card-link"
      >
        <ImageWrapper
          $isLarge={isLarge}
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
        >
          <ImageContainer
            key={currentImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            {currentMediaType === 'video' ? (
              <Video
                key={`video-${currentImageIndex}-${currentImage}`}
                $isLarge={isLarge}
                src={currentImage}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                draggable={false}
              />
            ) : (
              <Image
                $isLarge={isLarge}
                src={currentImage}
                alt={product.title}
                draggable={false}
                loading="lazy"
              />
            )}
          </ImageContainer>
          
          <ImageOverlay
            animate={{
              backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0)',
            }}
          />
          
          {hasMultipleImages && (
            <>
              <NavArrow
                $direction="left"
                onClick={goToPrevImage}
                aria-label="Previous image"
              />
              <NavArrow
                $direction="right"
                onClick={goToNextImage}
                aria-label="Next image"
              />
              <ImageCounter>
                <span>{currentImageIndex + 1}</span>
                <span style={{ opacity: 0.5 }}>/</span>
                <span>{allImages.length}</span>
              </ImageCounter>
            </>
          )}
          
          {discountPercent > 0 && (
            <DiscountBadge>-{discountPercent}%</DiscountBadge>
          )}
          
          <WishlistButton
            $isInWishlist={isWishlisted}
            onClick={handleWishlist}
            title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
          >
            <HeartIcon filled={isWishlisted} />
          </WishlistButton>
          
          <AddToCartButton
            onClick={handleAddToCart}
            title="Add to Cart"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </AddToCartButton>
        </ImageWrapper>

        <ProductInfo $isLarge={isLarge}>
          <ProductTitle>{product.title}</ProductTitle>
          <PriceContainer>
            <Price>{formatCurrency(product.price)}</Price>
          </PriceContainer>
        </ProductInfo>
      </Link>
      
      {showQuickAdd && (
        <QuickAddModal
          product={product}
          onClose={() => setShowQuickAdd(false)}
          onBuyNow={quickAddMode === 'buy'}
        />
      )}
    </CardContainer>
  );
};

export default ProductCard;